/**
 * Auth Callback Route
 *
 * Handles OAuth callback from Supabase.
 * Exchanges the code for a session and redirects to the app.
 */

import { Center, Heading, Spinner, Stack, Text } from '@cortex/ui';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/auth/callback')({
  component: AuthCallbackPage,
});

function AuthCallbackPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get the code from URL hash or search params
        const hashParams = new URLSearchParams(
          window.location.hash.substring(1)
        );
        const searchParams = new URLSearchParams(window.location.search);

        const code = searchParams.get('code');
        const errorParam = hashParams.get('error') ?? searchParams.get('error');
        const errorDescription =
          hashParams.get('error_description') ??
          searchParams.get('error_description');

        if (errorParam) {
          setError(errorDescription ?? errorParam);
          return;
        }

        if (code) {
          // Exchange code for session
          const { error: exchangeError } =
            await supabase.auth.exchangeCodeForSession(code);

          if (exchangeError) {
            setError(exchangeError.message);
            return;
          }
        }

        // Redirect to app
        navigate({ to: '/' });
      } catch {
        setError('An unexpected error occurred during authentication');
      }
    };

    handleCallback();
  }, [navigate]);

  if (error) {
    return (
      <Center className="bg-background min-h-screen">
        <Stack align="center" gap="sm">
          <Heading level={1} className="text-xl font-semibold">
            Authentication Error
          </Heading>
          <Text color="error" className="mt-2">
            {error}
          </Text>
          <Link
            to="/login"
            className="text-primary mt-4 inline-block hover:underline"
          >
            Return to login
          </Link>
        </Stack>
      </Center>
    );
  }

  return (
    <Center className="bg-background min-h-screen">
      <Stack align="center" gap="sm">
        <Spinner size="md" className="mx-auto" />
        <Text color="secondary" className="mt-4">
          Completing authentication...
        </Text>
      </Stack>
    </Center>
  );
}
